import { FC, useState } from 'react';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useQueryClient } from 'react-query';
import {
  Button,
  ButtonPrimary,
  Form,
  InputCheckbox,
  InputPhone,
  InputText,
  ModalActions,
  useSnackbar
} from '@lambdacurry/component-library';
import { addressInitialValues, nameInitialValues } from 'lib/forms/initialValues';
import { addressValidation, emailAddressValidation, nameValidation } from 'lib/forms/validation';
import { AddressInputGroup, NameInputGroup } from '../../InputGroups';
import { useStripeConfirmSetupIntent } from '../../../hooks';
import { StripePaymentElement } from '../../Forms';

const initialValues = {
  phone: '',
  email: '',
  is_default: false,
  ...nameInitialValues,
  ...addressInitialValues
};

const validationSchema = Yup.object({
  ...nameValidation,
  ...emailAddressValidation,
  ...addressValidation,
  phone: Yup.string().nullable(),
  is_default: Yup.boolean()
});

type FormValues = typeof initialValues;

export interface AddPaymentMethodFormProps {
  redirectPath?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

export const AddPaymentMethodForm: FC<AddPaymentMethodFormProps> = ({ redirectPath, onSave, onCancel }) => {
  const queryClient = useQueryClient();
  const { confirm } = useStripeConfirmSetupIntent({ redirectPath });
  const { addSnackbar } = useSnackbar();
  const [stripeStatus, setStripeStatus] = useState<'loading' | 'ready'>('loading');

  const handleSubmit = (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    confirm(values, {
      onSuccess: () => {
        addSnackbar('Payment method added!', { variant: 'success' });
        queryClient.refetchQueries('MyPaymentMethods');
        if (onSave) onSave();
      },
      onError: () => formikHelpers.setSubmitting(false)
    });
  };

  return (
    <Form<FormValues> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {formikProps => (
        <>
          <NameInputGroup />

          <InputText name="email" label="Email" />
          <InputPhone name="phone" label="Phone" />

          <AddressInputGroup />

          <div className="mt-4">
            <StripePaymentElement onReady={() => setStripeStatus('ready')} />
          </div>

          <div className="mt-2 mb-5">
            <InputCheckbox name="is_default" label="Make this your default payment method" />
          </div>

          <ModalActions className="!mt-4">
            <ButtonPrimary
              type="submit"
              data-cy="submit"
              className="!flex-grow-0"
              disabled={stripeStatus !== 'ready' || formikProps.isSubmitting}
            >
              {formikProps.isSubmitting ? <>Saving...</> : <>Save Payment Method</>}
            </ButtonPrimary>
            {onCancel && (
              <Button className="!flex-grow-0" onClick={() => onCancel()} disabled={formikProps.isSubmitting}>
                Cancel
              </Button>
            )}
          </ModalActions>
        </>
      )}
    </Form>
  );
};
